import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import dayjs from 'dayjs'

import { loggedWorkApi } from '@core/app/api/loggedWorkApi'
import { RootState } from '@core/app/store/store'
import { ShortJob } from '@core/app/types/absenceApiType'
import {
    CalendarTrip,
    childProject,
    Job,
    JobsSettings,
    LoggedWorkItem,
    LoggedWorkSummary,
} from '@core/app/types/logworkApiType'

export type ColumnTypes =
    | 'date'
    | 'job'
    | 'subProject'
    | 'article'
    | 'articleGroup'
    | 'status'
    | 'info'
    | 'duration'
    | 'break'
    | 'qty'
    | 'salary'
    | 'invoice'

interface FilterItem {
    id: string
    label: string
    value: number[] | number | boolean | null
}

interface LoggedWorkState {
    choosedDate: string
    viewType: 'week' | 'month' | 'year'
    loggedWork: LoggedWorkItem[]
    filterSourceLoggedWork: LoggedWorkItem[]
    isLoggedWorkLoaded: boolean
    loading: boolean
    firstJobYear: number
    tableColumns: {
        id: number
        type: ColumnTypes
        label: string
        visibility: boolean
        width: string
        mobileWidth?: string
        manuallyHidden: boolean
        autoHidden: boolean
    }[]
    jobs: Job[]
    shortJobs: ShortJob[]
    fullInfoJobs: Job[]
    childProjects: childProject[]
    articleGroups: { articleGroupID: number; name: string }[]
    selectedWorks: LoggedWorkItem[]
    summary: LoggedWorkSummary | null
    filters: {
        articles?: FilterItem
        jobs?: FilterItem
        subProjects?: FilterItem
        trips?: FilterItem
        attachments?: FilterItem
        notes?: FilterItem
        // 1 - To approval, 2 - Approved, 3 - Paid
        status?: FilterItem
    } | null
    quickFiltersUsed: string[]
    trips: CalendarTrip[]
    employeeSettings: JobsSettings
}

const defaultTableColumns = [
    { id: 1, type: 'date', label: 'Date', visibility: true, width: '133px', manuallyHidden: false, autoHidden: false },
    {
        id: 2,
        type: 'job',
        label: 'Job',
        visibility: true,
        width: '271px',
        mobileWidth: '180px',
        manuallyHidden: false,
        autoHidden: false,
    },
    {
        id: 3,
        type: 'subProject',
        label: 'Sub project',
        visibility: true,
        width: '170px',
        manuallyHidden: false,
        autoHidden: false,
    },
    {
        id: 4,
        type: 'article',
        label: 'Article',
        visibility: true,
        width: '271px',
        mobileWidth: '180px',
        manuallyHidden: false,
        autoHidden: false,
    },
    {
        id: 5,
        type: 'articleGroup',
        label: 'Article group',
        visibility: true,
        width: '147px',
        manuallyHidden: false,
        autoHidden: false,
    },
    {
        id: 6,
        type: 'status',
        label: 'Status',
        visibility: true,
        width: '155px',
        manuallyHidden: false,
        autoHidden: false,
    },
    { id: 7, type: 'info', label: 'Info', visibility: true, width: '125px', manuallyHidden: false, autoHidden: false },
    {
        id: 8,
        type: 'duration',
        label: 'Duration',
        visibility: true,
        width: '108px',
        manuallyHidden: false,
        autoHidden: false,
    },
    {
        id: 9,
        type: 'break',
        label: 'Break',
        visibility: true,
        width: '180px',
        manuallyHidden: false,
        autoHidden: false,
    },
    { id: 10, type: 'qty', label: 'Qty', visibility: true, width: '85px', manuallyHidden: false, autoHidden: false },
    {
        id: 11,
        type: 'salary',
        label: 'Salary',
        visibility: true,
        width: '140px',
        manuallyHidden: false,
        autoHidden: false,
    },
    {
        id: 12,
        type: 'invoice',
        label: 'Invoice',
        visibility: true,
        width: '140px',
        manuallyHidden: false,
        autoHidden: false,
    },
]

const loadSettingsFromLocalStorage = () => {
    const storedSettings = localStorage.getItem('settings')
    if (storedSettings) {
        try {
            const parsedSettings = JSON.parse(storedSettings)
            // Merge stored columns with default columns to ensure mobileWidth is added
            const mergedColumns = defaultTableColumns.map((defaultCol) => {
                const storedCol = parsedSettings.columns?.find((col: any) => col.type === defaultCol.type)
                return storedCol ? { ...defaultCol, ...storedCol, mobileWidth: defaultCol.mobileWidth } : defaultCol
            })
            return {
                columns: mergedColumns,
                filters: parsedSettings.filters ?? null,
            }
        } catch {
            console.error('Failed to parse settings from localStorage')
        }
    }
    // Default values
    return { columns: defaultTableColumns, filters: null }
}

const saveSettingsToLocalStorage = (settings: {
    columns?: LoggedWorkState['tableColumns']
    filters?: LoggedWorkState['filters'] | null
}) => {
    localStorage.setItem('settings', JSON.stringify(settings))
}

const resolveFirstJobYear = (jobs: Job[], apiYear?: number, fallbackYear = dayjs().year()): number => {
    let earliest = fallbackYear

    if (typeof apiYear === 'number' && apiYear > 0) {
        earliest = Math.min(earliest, apiYear)
    }

    jobs.forEach((job) => {
        if (job.fromDate) {
            const year = dayjs(job.fromDate).year()
            if (Number.isFinite(year)) earliest = Math.min(earliest, year)
        }

        job.childProjects?.forEach((project) => {
            if (!project.fromDate) return
            const year = dayjs(project.fromDate).year()
            if (Number.isFinite(year)) earliest = Math.min(earliest, year)
        })
    })

    return earliest
}

const dateFromOpenWorkLink = () => {
    if (typeof window === 'undefined') return null
    const params = new URLSearchParams(window.location.search)
    const date = params.get('date')
    const id = Number(params.get('openWorkHourId'))
    if (!date || !Number.isFinite(id) || id <= 0) return null
    const parsed = dayjs(date)
    return parsed.isValid() ? parsed.toISOString() : null
}

const initialState: LoggedWorkState = {
    choosedDate: dateFromOpenWorkLink() || localStorage.getItem('loggedWork_date') || dayjs().toISOString(),
    viewType: 'month',
    loggedWork: [],
    filterSourceLoggedWork: [],
    loading: false,
    isLoggedWorkLoaded: false,
    firstJobYear: dayjs().year(),
    tableColumns: loadSettingsFromLocalStorage().columns,
    filters: loadSettingsFromLocalStorage().filters,
    jobs: [],
    shortJobs: [],
    selectedWorks: [],
    summary: null,
    quickFiltersUsed: [],
    trips: [],
    articleGroups: [],
    childProjects: [],
    employeeSettings: {} as JobsSettings,
    fullInfoJobs: [],
}

const loggedWorkSlice = createSlice({
    name: 'loggedWork',
    initialState,
    reducers: {
        setChoosedDate(state, action) {
            if (state.choosedDate === action.payload) return
            state.choosedDate = action.payload
            localStorage.setItem('loggedWork_date', action.payload)
            state.loggedWork = []
            state.summary = null
            state.loading = true
            state.isLoggedWorkLoaded = false
        },
        setViewType(state, action) {
            if (state.viewType === action.payload) return
            state.viewType = action.payload
            state.loggedWork = []
            state.summary = null
            state.loading = true
            state.isLoggedWorkLoaded = false
        },
        updateTableColumns(state, action: PayloadAction<{ type: string; visibility?: boolean; newIndex?: number }>) {
            const { type, visibility, newIndex } = action.payload
            const columnIndex = state.tableColumns.findIndex((col) => col.type === type)

            if (columnIndex !== -1) {
                if (visibility !== undefined) {
                    state.tableColumns[columnIndex].visibility = visibility
                    state.tableColumns[columnIndex].manuallyHidden = !visibility
                }

                if (newIndex !== undefined && newIndex >= 0 && newIndex < state.tableColumns.length) {
                    const [movedColumn] = state.tableColumns.splice(columnIndex, 1)
                    state.tableColumns.splice(newIndex, 0, movedColumn)
                }

                saveSettingsToLocalStorage({ columns: state.tableColumns, filters: state.filters })
            }
        },
        resetColumnsOrder(state) {
            const sortedColumns = [...state.tableColumns].sort((a, b) => a.id - b.id)
            state.tableColumns = sortedColumns

            saveSettingsToLocalStorage({ columns: sortedColumns, filters: state.filters })
        },
        checkWork(state, action: PayloadAction<LoggedWorkItem>) {
            state.selectedWorks = [...state.selectedWorks, action.payload]
        },
        uncheckWork(state, action: PayloadAction<LoggedWorkItem>) {
            const id = action.payload.workHourId
            state.selectedWorks = state.selectedWorks.filter((item) => item.workHourId !== id)
        },
        setSelectedWorks(state, action: PayloadAction<LoggedWorkItem[]>) {
            state.selectedWorks = action.payload
        },
        setQuickFilter(state, action: PayloadAction<string>) {
            if (state.quickFiltersUsed.includes(action.payload)) return
            state.quickFiltersUsed.push(action.payload)
        },
        removeQuickFilter(state, action: PayloadAction<string>) {
            state.quickFiltersUsed = state.quickFiltersUsed.filter((f) => !f.includes(action.payload))
        },
        setFilter(
            state,
            action: PayloadAction<{ id: string; value: number[] | number | boolean | null; label: string }>
        ) {
            if (!state.filters) {
                state.filters = {}
            }
            // @ts-ignore
            state.filters[action.payload.id] = {
                id: action.payload.id,
                value: action.payload.value,
                label: action.payload.label,
            }
            saveSettingsToLocalStorage({ filters: state.filters, columns: state.tableColumns })
        },
        removeFilter(state, action: PayloadAction<string>) {
            // @ts-ignore
            if (state.filters && state.filters[action.payload]) {
                // @ts-ignore
                delete state.filters[action.payload]
                saveSettingsToLocalStorage({ filters: state.filters, columns: state.tableColumns })
            }
        },
        clearFilters(state) {
            state.filters = null
            saveSettingsToLocalStorage({ filters: {}, columns: state.tableColumns })
        },
    },
    extraReducers(builder) {
        builder.addMatcher(loggedWorkApi.endpoints.getData.matchPending, (state, action) => {
            if (action.meta.arg.originalArgs?.facetsOnly) return
            state.loading = true
            state.isLoggedWorkLoaded = false
        }),
            builder.addMatcher(loggedWorkApi.endpoints.getData.matchRejected, (state, action) => {
                if (action.meta.arg.originalArgs?.facetsOnly) return
                state.loading = false
                state.isLoggedWorkLoaded = false
            }),
            builder.addMatcher(loggedWorkApi.endpoints.getData.matchFulfilled, (state, action) => {
                const { work, trips, jobsAll, employeeSettings, jobs, firstJobYear } = action.payload
                const args = action.meta.arg.originalArgs
                const isFacetsOnly = !!args?.facetsOnly

                if (work) {
                    const loggedWork = Object.values(work.loggedWork).flat() as LoggedWorkItem[]

                    if (isFacetsOnly) {
                        state.filterSourceLoggedWork = loggedWork
                        return
                    }

                    const hasWorkFilters = !!(
                        (Array.isArray(args?.workJobs) && args.workJobs.length > 0) ||
                        (Array.isArray(args?.workSubProjects) && args.workSubProjects.length > 0) ||
                        (Array.isArray(args?.workArticleIds) && args.workArticleIds.length > 0) ||
                        (Array.isArray(args?.workStatus) && args.workStatus.length > 0) ||
                        (Array.isArray(args?.workTripIds) && args.workTripIds.length > 0) ||
                        args?.workNotes ||
                        args?.workTrips ||
                        args?.workAttachments
                    )

                    const dateCount: Record<string, number> = {}
                    loggedWork.forEach((item) => {
                        if (!item.fromDate) return
                        dateCount[item.fromDate] = (dateCount[item.fromDate] || 0) + 1
                    })
                    state.loggedWork = loggedWork.map((item, index, array) => {
                        if (!item.fromDate) return item
                        const isSecondary =
                            dateCount[item.fromDate] > 1 &&
                            array.findIndex((d) => d.fromDate === item.fromDate) !== index
                        const isLastChildren =
                            dateCount[item.fromDate] > 1 &&
                            array.findLastIndex((d) => d.fromDate === item.fromDate) === index
                        const isChildrensExist = dateCount[item.fromDate] > 1
                        return { ...item, isSecondary, isLastChildren, isChildrensExist }
                    })

                    if (!hasWorkFilters) {
                        state.filterSourceLoggedWork = state.loggedWork
                    }

                    state.summary = work.summary
                    state.trips = trips
                    state.shortJobs = work.jobs
                    state.childProjects = work.childProjects
                    state.employeeSettings = employeeSettings

                    if (work.groups) {
                        state.articleGroups = Object.values(work.groups)
                    }

                    const uniqueJobIds = new Set(loggedWork.map((item) => item.jobId))
                    const hasArticleGroups = loggedWork.some((item) => item.articleGroupId !== 0)
                    const hasSalary = loggedWork.some((item) => item.salary !== 'hidden')
                    const hasInvoice = loggedWork.some((item) => item.invoice !== 'hidden')
                    const hasChildProjects = loggedWork.some((item) => item.childProjectId !== 0)

                    state.tableColumns.forEach((column) => {
                        if (!column.manuallyHidden) {
                            switch (column.type) {
                                case 'job':
                                    column.autoHidden = uniqueJobIds.size <= 1
                                    break
                                case 'articleGroup':
                                    column.autoHidden = !hasArticleGroups
                                    break
                                case 'salary':
                                    column.autoHidden = !hasSalary
                                    break
                                case 'invoice':
                                    column.autoHidden = !hasInvoice
                                    break
                                case 'subProject':
                                    column.autoHidden = !hasChildProjects
                                    break
                            }
                        }

                        column.visibility = !column.autoHidden && !column.manuallyHidden
                    })

                    saveSettingsToLocalStorage({ columns: state.tableColumns, filters: state.filters })
                }

                if (!isFacetsOnly) {
                    state.jobs = jobsAll
                    state.fullInfoJobs = jobs || []
                    state.firstJobYear = Math.min(
                        state.firstJobYear,
                        resolveFirstJobYear(jobs || [], firstJobYear, state.firstJobYear)
                    )
                    state.loading = false
                    state.isLoggedWorkLoaded = true
                }
            })
    },
})

// Helper function to get column width based on mobile state
export const getColumnWidth = (column: { width: string; mobileWidth?: string }, isMobile: boolean): string => {
    const result = isMobile && column.mobileWidth ? column.mobileWidth : column.width

    return result
}

// Helper function to clear localStorage settings (for debugging)
export const clearLocalStorageSettings = () => {
    localStorage.removeItem('settings')
    console.log('LocalStorage settings cleared')
}

export default loggedWorkSlice.reducer
export const {
    setChoosedDate,
    setViewType,
    updateTableColumns,
    checkWork,
    uncheckWork,
    resetColumnsOrder,
    setQuickFilter,
    removeQuickFilter,
    setFilter,
    removeFilter,
    clearFilters,
    setSelectedWorks,
} = loggedWorkSlice.actions
export const storeChoosedDate = (state: RootState) => state.loggedWork.choosedDate
export const storeViewType = (state: RootState) => state.loggedWork.viewType
export const storeLoggedWork = (state: RootState) => state.loggedWork.loggedWork
export const storeFilterSourceLoggedWork = (state: RootState) => state.loggedWork.filterSourceLoggedWork
export const storeLoading = (state: RootState) => state.loggedWork.loading
export const storeTableColumns = (state: RootState) => state.loggedWork.tableColumns
export const storeJobs = (state: RootState) => state.loggedWork.jobs
export const storeShortJobs = (state: RootState) => state.loggedWork.shortJobs
export const storeIsLoggedWorkLoaded = (state: RootState) => state.loggedWork.isLoggedWorkLoaded
export const storeSelectedWorks = (state: RootState) => state.loggedWork.selectedWorks
export const storeSummary = (state: RootState) => state.loggedWork.summary
export const storeQuickFiltersUsed = (state: RootState) => state.loggedWork.quickFiltersUsed
export const storeFilters = (state: RootState) => state.loggedWork.filters
export const storeTrips = (state: RootState) => state.loggedWork.trips
export const storeArticleGroups = (state: RootState) => state.loggedWork.articleGroups
export const storeChildProjects = (state: RootState) => state.loggedWork.childProjects
export const storeEmployeeSettings = (state: RootState) => state.loggedWork.employeeSettings
export const storeFullInfoJobs = (state: RootState) => state.loggedWork.fullInfoJobs
export const storeLoggedWorkFirstJobYear = (state: RootState) => state.loggedWork.firstJobYear
